import React from 'react';
import { Link, useLocation } from "react-router-dom";
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import Button from '@mui/material/Button';
import { TextField, Tooltip } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';

import { Item, Item2 } from '../../styles';
import { getPreview } from '../../services/Spectrum';


import Header from '../../components/HeaderComponent';
import ErrorDialog from '../../components/ErrorDialog';
import ButtonWithHelp from '../../components/ButtonWithHelp';
import RefreshError from '../../schemas/Exception/RefreshError';
import InternalError from '../../schemas/Exception/InternalError';
import UnusualError from "../../schemas/Exception/UnusualError";
import { SolventControl } from '../../components/SolventControl';
import { fetchGet, fetchPost } from '../../services/GettingData';
import HelpIconWithHint from '../../components/HelpIconWithHint';
import SpectrumTypeControl from '../../components/SpectrumTypeControl';
import { SearchDirection } from '../../schemas/Spectrum/SearchDirection';
import ISpectrumPreviewModel from '../../schemas/Spectrum/ISpectrumPreviewModel';
import NotAuthorizedError from '../../schemas/Exception/NotAuthorizedError';
import AtomsArray from '../../components/AtomsArray';

import '../../App.css';

type SpectrumSearchProps = {
    spectrumPreview: ISpectrumPreviewModel
};

export const PredictCompoundPreview: React.FC<SpectrumSearchProps> = (props) => {
    const location = useLocation();

    const [isDialogOpened, setDialogOpened] = React.useState<boolean>(false);

    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [showMessage, setShowMessage] = React.useState<string>('');
    const spectrumSearchButtonRef = React.useRef<HTMLButtonElement>(null);

    const [spectrumSearch, setSpectrumSearch] = React.useState<ISpectrumPreviewModel>({
        peaks:
            (props as any).location.state?.spectrumPreview?.peaks.sort((a: number, b: number) => a - b),
        spectrum_type: '',
        solvent: undefined,
        is_complete: false,
        graph: (props as any).location.state?.spectrumPreview?.graph,
        spectrumString: (props as any).location.state?.spectrumPreview?.searchDirection === SearchDirection.BySpectrumString ?
            (props as any).location.state?.spectrumPreview?.spectrumString : '',
        structure: '',

    });

    const [includedAtoms, setIncludedAtoms] = React.useState<string[]>([]);
    const [excludedAtoms, setExcludedAtoms] = React.useState<string[]>([]);

    const handleIncludedAtomsChange = (selectedAtoms: string[]) => {
        setIncludedAtoms(selectedAtoms);
    };

    const handleExcludedAtomsChange = (selectedAtoms: string[]) => {
        // Filter out any atoms that are already in the includedAtoms list
        const filteredAtoms = selectedAtoms.filter(atom => !includedAtoms.includes(atom));
        setExcludedAtoms(filteredAtoms);
    };

    const removeAt = (array: number[], atIndex: number) => {
        array.splice(atIndex, 1);
        return array;
    }

    const closeErrorDialog = () => setDialogOpened(false);

    const showError = (errorMessage: string) => {
        setErrorMessage(errorMessage);
        setDialogOpened(true);
    }

    const deletePeak = (id: number) => {
        setSpectrumSearch((prev: any) => ({
            ...prev,
            'peaks': removeAt(prev.peaks, id)
        }));
    }

    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'ID',
            width: 50,
            hide: true
        },
        {
            field: 'shift',
            headerName: 'Shift',
            type: 'number',
            width: 150,
            sortable: false,
            editable: !spectrumSearch?.is_complete,
            valueFormatter: ({ value }) => String(value).replace(',', '.')
        },
        {
            field: 'actions',
            type: 'actions',
            width: 200,
            sortable: false,
            renderCell: (params: GridCellParams) => {
                const onDelClick = (e: any) => {
                    e.stopPropagation();
                    deletePeak(Number(params.id));
                }
                return <div style={{ width: '2em', height: '1em' }}
                    onClick={onDelClick}>X</div>;
            }
        },
    ];

    const pngString = encodeURIComponent(spectrumSearch.graph ? spectrumSearch.graph : '');
    const dataUri = `data:image/png;base64,${pngString}`;


    const reduceCalculated = (prev: ISpectrumPreviewModel): ISpectrumPreviewModel => {
        let peaks = prev.searchDirection === SearchDirection.ByPeaks ? prev.peaks : [];
        let spectrumString = prev.searchDirection === SearchDirection.BySpectrumString ? prev.spectrumString : '';
        return { ...prev, peaks: peaks, spectrumString: spectrumString };
    }

    const editSpectrum = () => {
        setSpectrumSearch(prev => ({
            ...reduceCalculated(prev),
            is_complete: false
        }));
    }


    const onCellEditCommit = (cellData: any) => {
        if (cellData.field === 'shift') {
            let peaks = spectrumSearch.peaks;
            peaks[cellData.id] = Number(cellData.value);
            setSpectrumSearch(prev => ({
                ...prev,
                'peaks': peaks,
            }));
        }

        if (cellData.field === 'int') {
        }
    }

    const rows = [];
    for (let i = 0; i < spectrumSearch.peaks.length; i++) {
        rows.push({ id: i, shift: spectrumSearch.peaks[i] });
    }




    const addPeak = (): void => {
        setSpectrumSearch(prev => ({
            ...prev,
            'peaks': [...prev.peaks, 0],
        }));
    }

    const changeSolventHandler = (e: SelectChangeEvent) => {
        setSpectrumSearch((prev: ISpectrumPreviewModel) => ({ ...prev, 'solvent': (e.target.value === 'any' ? undefined : e.target.value) }));
    }

    return (
        <Grid container md={12} spacing={0} className='main-frame'>
            <Grid container md={12}>
                <Item2><div style={{ height: '2em' }}></div></Item2>
            </Grid>

            <ErrorDialog isDialogOpened={isDialogOpened}
                errorMessage={errorMessage}
                warningMessage={showMessage}
                onClose={closeErrorDialog} />

            <Grid xs={12}>
                <Header title={'NMR predictions'}
                    helpAddress='help#htuss'
                    showLogin={true} />
            </Grid>

            <Grid container xs={12} spacing={0} style={{ marginTop: '.5em' }}>
                <Grid xs={4}>
                </Grid>
                <Grid xs={4}>
                </Grid>
                <Grid md={4} xs={12} sm={12} className="spectrum-type-container" style={{ paddingLeft: '2em' }}>
                    <Item2 style={{ fontSize: '1em', textAlign: 'left' }}>
                        <div style={{ marginTop: '1em' }}>
                            <Grid container alignItems="center" justifyContent="center" >
                                <Grid xs={11}>
                                    <SpectrumTypeControl
                                        disabled={true}
                                        selected={'C_NMR'} />
                                </Grid>
                                <Grid xs={1} style={{ verticalAlign: 'middle' }}>
                                    <HelpIconWithHint title={''}
                                        text={'You need this field ONLY if you insert peaks to the shift-intensity table. In this case choose spectrum type, solvent and add as much peaks as you want. Otherwise do not care about these fields (spectrum type, solvent)'}
                                        handleOpen={() => { }} />
                                </Grid>
                            </Grid>
                        </div>
                        <div style={{ marginTop: '1em' }}>
                            <Grid container alignItems="center" justifyContent="center" >
                                <Grid xs={11}>
                                    <SolventControl anyPossible={true}
                                        onChange={changeSolventHandler}
                                        disabled={true}
                                        selected={'CDCl_3'} />
                                </Grid>

                                <Grid xs={1} style={{ verticalAlign: 'middle' }}>
                                    <HelpIconWithHint title={''}
                                        text={'Here you can change solvent'}
                                        handleOpen={() => { }} />
                                </Grid>
                            </Grid>

                        </div>
                    </Item2>
                </Grid>
            </Grid>

            <Grid container md={12} spacing={0} style={{ marginTop: '2em' }}>
                <Grid container
                    style={{ marginBottom: '2em' }}
                    spacing={0}
                    md={8}
                    xs={12}
                    direction="column"
                    alignItems="center"
                    justifyContent="center">

                    {spectrumSearch.graph ?
                        <Grid xs={12}
                            spacing={0}
                            direction="column"
                            alignItems="center"
                            justifyContent="center">
                            <Item style={{ width: '100%', marginRight: '.5em' }} >
                                <div className='graph-container' style={{ paddingBottom: '0px', paddingTop: '2px' }}>
                                    <img className='graph-qc' src={dataUri} style={{ maxWidth: '100%' }} />
                                </div>
                            </Item>
                        </Grid> : ''}

                </Grid>

                <Grid md={4} xs={12} style={{ marginTop: '0px' }} className='pamd1' spacing={1}>
                    <Item style={{ fontSize: '2em', paddingTop: '0px', paddingBottom: '0px' }}>
                        <Box sx={{ height: 300, width: '100%' }}>
                            <DataGrid
                                rows={rows}
                                columns={columns}
                                disableColumnMenu
                                onCellEditCommit={onCellEditCommit}
                            />
                        </Box>
                        <Tooltip arrow title='Manual insertion of peaks and intensity'>
                            <Button onClick={() => addPeak()}
                                variant="contained"
                                style={{ marginBottom: '1em', marginTop: '1em' }}>Add Peak</Button>
                        </Tooltip>
                    </Item>
                </Grid>
            </Grid>

            <Grid container md={12} spacing={2} style={{ marginTop: '0em' }}>
                <Grid md={4}>
                    <AtomsArray
                        title={'Elements must be in molecule'}
                        onChange={handleIncludedAtomsChange}
                        selected={includedAtoms}
                    />
                </Grid>
                <Grid md={4}>
                    <AtomsArray
                        title={'Elements can not be in molecule'}
                        onChange={handleExcludedAtomsChange}
                        exclude={includedAtoms}
                        selected={excludedAtoms}
                    />
                </Grid>
            </Grid>

            <Grid container md={12} spacing={0} style={{ marginTop: '2em' }}>
                <Grid md={4} display={{ xs: 'none', md: 'block' }}></Grid>
                <Grid md={4} xs={12} style={{ minWidth: '220px' }}>
                    <Item2 style={{ fontSize: '2em', paddingTop: '0px', paddingBottom: '0px' }}>
                        {spectrumSearch.is_complete &&
                            <ButtonWithHelp variant='contained'
                                tooltip='Serious modifications of the request'
                                title='Edit spectrum'
                                text='Press here if you want to modify something except for solvent or add/remove peak'
                                onClick={editSpectrum} 
                                disabled={false} />
                        }
                    </Item2>
                </Grid>
                <Grid md={4} xs={12} className='pamd1'>
                    <Item2 className="responsive-submit-container" style={{ fontSize: '2em', paddingTop: '0px', paddingBottom: '0px' }}>
                        <div>
                            <Link className='MuiButton' to={{
                                pathname: `/predict-compound-result`,
                                state: { spectrumPreview: spectrumSearch, includedAtoms: includedAtoms, excludedAtoms: excludedAtoms }
                            }}>
                                <Tooltip arrow title={'Predict structure using AI'}>
                                    <Button sx={{
                                        "&.MuiButton-contained": {
                                            backgroundColor: "success.main"
                                        },
                                        "&.MuiButton-contained:hover": {
                                            backgroundColor: "success.dark"
                                        },
                                        "&.MuiButton-contained:focus": {
                                            backgroundColor: "success.main"
                                        },
                                        "&.MuiButton-contained.Mui-focusVisible": {
                                            backgroundColor: "success.main",
                                            boxShadow: "none"
                                        },
                                        "&:focus": {
                                            outline: "none",
                                            backgroundColor: "success.main"
                                        },
                                        "&.Mui-focused": {
                                            backgroundColor: "success.main"
                                        }
                                    }} ref={spectrumSearchButtonRef}
                                        color="success" variant="contained">
                                        {'Predict Structure'}
                                    </Button>
                                </Tooltip>
                            </Link>
                            <HelpIconWithHint title={''}
                                text={'Use AI to predict molecular structure from your NMR data'}
                                handleOpen={() => { }} />
                        </div>

                    </Item2>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default PredictCompoundPreview;